import { useState, useMemo, Component, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Upload, X, Check, Download, Info, Trash2, AlertTriangle, Undo2, Keyboard, CheckCircle2 } from 'lucide-react';
import { apiFetch, ApiClientError, API_BASE, handleUnauthorized } from '@/lib/api';
import { DATA_TYPE_SPECS, downloadCsv, type ColumnSpec } from '@/lib/import-specs';
import { EnvironmentBadge } from '@/components/EnvironmentBadge';
import { postInputReady } from '@/lib/api/publish';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';

type StoreOption = { store_id: string; store_name: string; store_code: string };

/** 専用の手入力画面が用意されているデータ種別 → 遷移先パス (Design レビュー HIGH 反映:
 *  「手入力」タブ削除後、CSVを用意できないユーザーへの代替導線が画面内に無かったため追加) */
const MANUAL_ENTRY_LINKS: Partial<Record<string, string>> = {
  labor: '/entry/labor',
  opex: '/entry/opex',
  delivery_sales: '/entry/delivery-sales',
  sales: '/entry/sales',
  delivery_details: '/entry/delivery-details',
};

type ImportError = { row: number; code?: string; column?: string; message: string; aggregated_count?: number; sample_rows?: number[] };
type ImportMode = 'upsert' | 'delete' | 'delete_rows';

type PreviewResponse = {
  import_job_id: string;
  data_type: string;
  mode?: ImportMode;
  status: string;
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  errors: ImportError[];
  preview: any[];
  replacements?: Array<{ store_code: string; period: string; existing_count: number }>;
  row_delete_matches?: number;
  store_operation_counts?: { renew: number; close: number; reopen: number };
  skipped_rows?: number;      // 閉店済みの店舗と一致して読み飛ばした行数 (店舗マスタのみ)
};

type CommitResponse = {
  import_job_id: string;
  mode?: ImportMode;
  inserted: number;           // 後方互換: inserted_rows + updated_rows の合計
  inserted_rows?: number;     // v0.2 D4: 実際に INSERT された operation 数
  updated_rows?: number;      // v0.2 D4: 実際に UPDATE された operation 数
  deleted?: number;
  skipped_rows?: number;      // 閉店済みの店舗と一致して読み飛ばした行数 (店舗マスタのみ)
  error_count: number;
  errors: ImportError[];
  status: string;
};

/** v0.2.5 (S-8): マスタCSV取込コミットが承認ワークフロー対象テナントの場合、
 *  commit API は即時コミットせずこの形を返す (apps/api/src/routes/imports.ts の
 *  POST /:job_id/commit、承認WFが無効なテナントでは発生しない)。 */
type PendingApprovalResponse = {
  import_job_id: string;
  status: 'pending_approval';
  approval_request_id: string;
};

type CommitApiResponse = CommitResponse | PendingApprovalResponse;

/** approval_request_id の有無で判別 (pending_approval 以外の status 値は自由文字列のため、
 *  status の等値比較ではなく型固有のプロパティで判別する) */
const isPendingApproval = (data: CommitApiResponse): data is PendingApprovalResponse =>
  'approval_request_id' in data;

/** commit API レスポンスから、画面に反映すべき状態を導出する純粋関数。
 *  CRITICAL バグ修正 (2026-08-03): 従来はこの分岐が無く、pending_approval レスポンス
 *  (errors フィールドを持たない) を CommitResponse として扱おうとして
 *  `commitResult.errors.length` で TypeError が発生し、画面が白紙になっていた。
 *  React state の setter から独立させ、apps/web/src/pages/__tests__/ImportsPage.test.ts で
 *  直接ユニットテストできるようにしている (StoreMultiSelect.test.ts と同じ方針)。 */
export type CommitOutcome =
  | { kind: 'pending_approval'; requestId: string; message: string }
  | { kind: 'result'; commitResult: CommitResponse; message: string };

export const deriveCommitOutcome = (
  data: CommitApiResponse,
  ctx: { previewValidRows?: number; dataType: string },
): CommitOutcome => {
  if (isPendingApproval(data)) {
    return {
      kind: 'pending_approval',
      requestId: data.approval_request_id,
      message: 'この取込は承認待ちです。承認者による承認後にコミットされます',
    };
  }
  // Design L-2 反映: delete_rows も含めて削除モード判定を広げる (将来 delete_rows で updated_rows を返す実装が入っても誤発火しない)
  const isDelete = data.mode?.startsWith('delete') ?? false;
  const verb = isDelete ? '削除' : '取込';
  // v0.2 D4: upsert 系で updated_rows > 0 の場合のみ内訳を表示 (Design M-1 反映で条件単純化)
  //   menus/マスタで実際に UPDATE が発生した時に「新規」「更新」の内訳をユーザーに提示。
  //   Design M-2 対応: menus は「新版 / 旧版クローズ」で history semantic を反映、マスタは「新規 / 更新」で分岐
  const showBreakdown = !isDelete && (data.updated_rows ?? 0) > 0;
  const isMenus = ctx.dataType === 'menus';
  const breakdown = showBreakdown
    ? isMenus
      ? ` (新版 ${data.inserted_rows} 件 / 旧版クローズ ${data.updated_rows} 件)`
      : ` (新規 ${data.inserted_rows} 件 / 更新 ${data.updated_rows} 件)`
    : '';
  // Design M-3 反映: 削除は削除件数、取込は「CSV 有効行数 (valid_rows)」を主軸に表示。
  //   inserted (=operation 数) との差 (menus の overlap 分) は breakdown 内に閉じ込める
  const count = isDelete ? (data.deleted ?? 0) : (ctx.previewValidRows ?? data.inserted);
  const skipped = (data.skipped_rows ?? 0) > 0 ? `。閉店済みの店舗の行 ${data.skipped_rows}件は変更がないため読み飛ばしました` : '';
  const message = data.error_count > 0
    ? `⚠ ${count}件を${verb}${breakdown}しました（エラー${data.error_count}件・詳細は下記のエラー一覧を確認してください）${skipped}`
    : `✅ ${count}件を${verb}${breakdown}しました${skipped}`;
  return { kind: 'result', commitResult: data, message };
};

type RollbackResponse = {
  import_job_id: string;
  data_type: string;
  status: string;
  deleted: number;
  message: string;
};

/** エラーコードを日本語ラベルに変換（「列の問題」か「データ内容の問題」かの見分けに使う） */
const ERROR_CODE_LABELS: Record<string, string> = {
  schema_error: '列の形式エラー',
  value_error: '値の形式エラー',
  not_found: '参照先が見つからない',
  forbidden_store: 'アクセス権限エラー',
  no_valid_menu_version: 'メニューマスタ未登録',
  period_exceeds_menu_validity: '期間がメニュー有効期間を超過',
  invalid_date: '日付形式エラー',
  // 2026-08-17: 従来は「形式が不正」と「カレンダー上に存在しない日付 (2月30日 等)」を
  // どちらも value_error / invalid_date で返しており、利用者が原因を切り分けられなかった。
  // backend (lib/dates.ts の dateProblemCode) が返す code に対応させること。
  invalid_calendar_date: '存在しない日付',
  invalid_date_format: '日付形式エラー',
  date_order: '日付の前後関係エラー',
  future_version_conflict: '未来履歴との重複',
  unsupported_type: '未対応のデータ種別',
  unknown_ingredient: '食材マスタ未登録',
  unknown_service: 'デリバリー業者未登録',
  unknown_cost_category: 'コスト分類マスタ未登録',
  duplicate_in_csv: 'CSV 内でキー重複',
  batch_failure: '一括取込中止 (整合性エラー)',
  exception: 'システムエラー',
  duplicate_renewal: 'リニューアル(renew)の重複取込',
  invalid_operation_type: '未対応の操作種別',
  // 店舗マスタの閉店・再開・リニューアル (docs/design/v04-store-close-reopen-consistency.md)
  use_reopen_operation: '閉店済みの店舗 (再開は reopen)',
  use_close_operation: '閉店は close で指定',
  closed_store_not_found: '閉店済みの店舗と一致しない',
  parent_closed: '元の親が停止中',
  has_active_children: '稼働中の子ノードあり',
  multiple_operations_same_store: '同じ店舗の操作が複数行',
  reopen_date_conflict: '再開日が履歴と重なる',
  inconsistent_closed_node: '閉店済み店舗の履歴が不整合',
  new_store_multiple_rows: '新しい店舗が複数行',
  rows_not_in_date_order: '複数行が開始日の古い順でない',
  date_before_current_version: '現在の版より前の開始日',
  active_node_exists: '同じコードの稼働中店舗あり',
};

/** 履歴管理型データ種別 (削除モード非対応) */
const DELETE_MODE_UNSUPPORTED = new Set(['menus', 'kpi_thresholds']);

/** エラー一覧テーブル（プレビュー時・コミット時で共用） */
function ImportErrorTable({ errors, totalErrorCount }: { errors: ImportError[]; totalErrorCount: number }) {
  return (
    <div>
      <h3 className="text-h3 mb-2">
        エラー一覧{totalErrorCount > errors.length ? `（先頭${errors.length}件 / 全${totalErrorCount}件）` : `（${errors.length}件）`}
      </h3>
      <div className="border border-neutral-line rounded max-h-60 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-neutral-panel text-neutral-sub sticky top-0">
            <tr>
              <th className="text-left px-2 py-2 w-14">行</th>
              <th className="text-left px-2 py-2 w-32">種別</th>
              <th className="text-left px-2 py-2 w-32">列</th>
              <th className="text-left px-2 py-2">内容</th>
            </tr>
          </thead>
          <tbody>
            {errors.map((e, i) => {
              // 集約行 (backend が code 別に「他 N 件」として圧縮したもの) は行番号列を「集約」と表示
              const isAggregated = e.aggregated_count !== undefined && e.aggregated_count > 0;
              return (
                <tr key={i} className={`border-t border-neutral-line ${isAggregated ? 'bg-neutral-panel' : ''}`}>
                  <td className="px-2 py-1 tabular-nums">
                    {isAggregated ? <span className="text-xs text-neutral-sub">集約</span> : e.row}
                  </td>
                  <td className="px-2 py-1 text-neutral-sub whitespace-nowrap">{e.code ? (ERROR_CODE_LABELS[e.code] ?? e.code) : '-'}</td>
                  <td className="px-2 py-1 font-mono text-neutral-sub whitespace-nowrap">{e.column ?? '-'}</td>
                  <td className="px-2 py-1 text-warning-text">
                    {e.message}
                    {isAggregated && e.sample_rows && e.sample_rows.length > 0 && (
                      <span className="text-xs text-neutral-sub ml-2">
                        (例: 行 {e.sample_rows.slice(0, 10).join(', ')}{e.sample_rows.length > 10 ? '…' : ''})
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 取込結果表示部分限定の ErrorBoundary。
 *  バックエンドのレスポンス形が想定と食い違った場合 (例: pending_approval 分岐漏れ) でも、
 *  画面全体を白紙にせずこの領域だけをエラー表示に落とし込む再発防止策。 */
class ImportResultBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  override state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  override componentDidCatch(error: unknown) {
    console.error('ImportsPage: 取込結果表示でエラーが発生しました', error);
  }
  override render() {
    if (this.state.hasError) {
      return (
        <div className="p-3 rounded bg-warning-bg border border-warning-border text-sm text-warning-text">
          結果表示中に問題が発生しました。取込自体は完了している可能性があります。ページを再読み込みして状態をご確認ください。
        </div>
      );
    }
    return this.props.children;
  }
}

// (v0.1.3: dataTypeList はコンポーネント内で category に応じて絞り込むため削除)

// ============================================================================
// ImportsPage 本体
//   v0.1.3: category prop で「取引データ」「マスタ」に絞込表示。
//     - undefined: 全種別表示 (legacy /imports)
//     - 'data': 取引データのみ、URL prefix /data/*
//     - 'master': マスタのみ、URL prefix /master/*
// ============================================================================
type ImportsPageProps = { category?: 'data' | 'master' };

/** カテゴリごとの表示順を明示 (Design レビュー H-3 対応)。
 *   master は「まず店舗を登録 → カテゴリ → メニュー → 食材 → 業者 → 閾値」の依存順で並べる。
 */
const DISPLAY_ORDER: Record<'data' | 'master', string[]> = {
  data:   ['sales', 'delivery_details', 'delivery_sales', 'labor', 'opex'],
  master: ['stores', 'menu_categories', 'menus', 'ingredient_master', 'delivery_services', 'kpi_thresholds'],
};

export function ImportsPage({ category }: ImportsPageProps = {}) {
  // カテゴリで DATA_TYPE_SPECS を絞り込み、DISPLAY_ORDER に従って並べる
  const dataTypeList = useMemo(() => {
    const all = Object.values(DATA_TYPE_SPECS).filter((d) => !category || d.category === category);
    if (!category) return all;
    const order = DISPLAY_ORDER[category];
    return order.map((k) => all.find((d) => d.key === k)).filter((d): d is NonNullable<typeof d> => Boolean(d));
  }, [category]);
  const initialDataType = dataTypeList[0]?.key ?? 'sales';
  const [dataType, setDataType] = useState(initialDataType);
  const [importMode, setImportMode] = useState<ImportMode>('upsert');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [commitResult, setCommitResult] = useState<CommitResponse | null>(null);
  const [pendingApproval, setPendingApproval] = useState<{ requestId: string } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showSpec, setShowSpec] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  // v0.3 (S-8データ公開ゲート): commit成功時のpreview.replacements (店舗×月の一覧) をここに退避する。
  // commit成功直後にsetPreview(null)でpreview自体がクリアされるため、その前に captureする必要がある
  const [committedScope, setCommittedScope] = useState<Array<{ store_code: string; period: string }>>([]);
  const [markingReady, setMarkingReady] = useState(false);
  const tenantFeatures = useAuthStore((s) => s.user?.tenant_features);
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const { data: allStores } = useQuery({
    queryKey: ['stores'],
    queryFn: () => apiFetch<StoreOption[]>('/stores'),
    enabled: tenantFeatures?.publish_gate_enabled === true,
  });

  const spec = DATA_TYPE_SPECS[dataType]!;
  const isDeleteMode = importMode === 'delete' || importMode === 'delete_rows';
  const isDeleteRowsMode = importMode === 'delete_rows';
  const isDeleteMonthMode = importMode === 'delete';
  const canDelete = !DELETE_MODE_UNSUPPORTED.has(dataType);

  // データ種別を切替える度にファイル選択もリセット。
  //   履歴管理型 (menus/kpi_thresholds) は delete 非対応なので upsert に強制戻し
  const changeDataType = (dt: string) => {
    setDataType(dt);
    setFile(null);
    setPreview(null);
    setCommitResult(null);
    setPendingApproval(null);
    setMessage(null);
    if (DELETE_MODE_UNSUPPORTED.has(dt)) setImportMode('upsert');
  };
  const changeImportMode = (m: ImportMode) => {
    setImportMode(m);
    setFile(null);
    setPreview(null);
    setCommitResult(null);
    setPendingApproval(null);
    setMessage(null);
  };

  const uploadPayload = file;

  const uploadMut = useMutation({
    mutationFn: async (): Promise<PreviewResponse> => {
      if (!uploadPayload) throw new Error('データがありません');
      const form = new FormData();
      form.append('file', uploadPayload);
      const token = localStorage.getItem('access_token');
      // category 指定時は /imports/data/... or /imports/master/... を使用
      // (v0.1.3: 将来のカスタムロールで URL レベル permission gate)
      const prefix = category ? `/${category}` : '';
      const url = `${API_BASE}/imports${prefix}/${dataType}?mode=${importMode}`;
      const res = await fetch(url, {
        method: 'POST',
        body: form,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      handleUnauthorized(res.status);
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new ApiClientError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? 'エラー');
      }
      return body.data;
    },
    onSuccess: (data) => { setPreview(data); setCommitResult(null); setMessage(null); },
    onError: (e) => { setMessage((e as Error).message); setPreview(null); },
  });

  const rollbackMut = useMutation({
    mutationFn: () => apiFetch<RollbackResponse>(`/imports/${commitResult!.import_job_id}/rollback`, {
      method: 'POST',
    }),
    onSuccess: (data) => {
      setMessage(`↩ ${data.deleted} 件を rollback しました。上書き前のデータは復元されません`);
      setCommitResult(null); // ボタンを消す
      setRollbackConfirmOpen(false);
    },
    onError: (e) => {
      setMessage(`❌ ${(e as Error).message}`);
      setRollbackConfirmOpen(false);
    },
  });

  const commitMut = useMutation({
    mutationFn: () => apiFetch<CommitApiResponse>(`/imports/${preview!.import_job_id}/commit`, {
      method: 'POST',
    }),
    onSuccess: (data) => {
      const outcome = deriveCommitOutcome(data, { previewValidRows: preview?.valid_rows, dataType });
      if (outcome.kind === 'pending_approval') {
        setCommitResult(null);
        setPendingApproval({ requestId: outcome.requestId });
      } else {
        setCommitResult(outcome.commitResult);
        setPendingApproval(null);
      }
      setMessage(outcome.message);
      // v0.3 (S-8): setPreview(null)でreplacementsが消える前に (店舗×月) 一覧を退避
      setCommittedScope(!isDeleteMode && preview?.replacements ? preview.replacements.map((r) => ({ store_code: r.store_code, period: r.period })) : []);
      setPreview(null);
      setFile(null);
      setDeleteConfirmOpen(false);
    },
    onError: (e) => { setMessage(`❌ ${(e as Error).message}`); setCommitResult(null); setPendingApproval(null); setDeleteConfirmOpen(false); },
  });

  const doCommit = () => {
    if (isDeleteMode) {
      setDeleteConfirmOpen(true);
    } else {
      commitMut.mutate();
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <h1 className="text-h1">{category === 'master' ? 'マスタ取込' : 'データ取込'}</h1>

      {/* データ種別選択＋テンプレートDL */}
      <div className="border border-neutral-line rounded-lg bg-white p-6 space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[250px]">
            <label className="block text-sm text-neutral-sub mb-1">データ種別</label>
            <select value={dataType} onChange={(e) => changeDataType(e.target.value)}
              disabled={!!preview}
              className="w-full px-3 py-2 rounded border border-neutral-line focus-ring">
              {dataTypeList.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <button
            onClick={() => downloadCsv(spec)}
            className="flex items-center gap-1 px-3 py-2 rounded border border-brand-red text-brand-red text-sm hover:bg-brand-red hover:text-white transition"
          >
            <Download size={16} /> テンプレートCSVをダウンロード
          </button>
        </div>

        {MANUAL_ENTRY_LINKS[dataType] && (
          <div className="flex items-center gap-1.5 text-sm text-neutral-sub">
            <Keyboard size={14} className="flex-shrink-0" />
            CSVをお持ちでない場合は
            <Link to={MANUAL_ENTRY_LINKS[dataType]!} className="text-brand-red hover:underline font-medium">
              {spec.label} 手入力
            </Link>
            画面から直接入力できます
          </div>
        )}

        {/* 取込モード選択 (upsert / delete_rows / delete) */}
        <div>
          <label className="block text-sm text-neutral-sub mb-1">
            取込モード
            {!canDelete && <span className="ml-2 text-xs text-neutral-sub">(このデータ種別は履歴管理のため削除モード非対応)</span>}
          </label>
          <div className="inline-flex rounded border border-neutral-line overflow-hidden" role="group" aria-label="取込モード">
            <button
              type="button"
              onClick={() => changeImportMode('upsert')}
              disabled={!!preview}
              aria-pressed={importMode === 'upsert'}
              className={`px-4 py-2 text-sm transition ${
                importMode === 'upsert'
                  ? 'bg-brand-red text-white'
                  : 'bg-white text-neutral-text hover:bg-neutral-panel'
              }`}
            >
              取込 (Upsert)
            </button>
            <button
              type="button"
              onClick={() => changeImportMode('delete_rows')}
              disabled={!!preview || !canDelete}
              aria-pressed={importMode === 'delete_rows'}
              className={`px-4 py-2 text-sm transition border-l border-neutral-line ${
                importMode === 'delete_rows'
                  ? 'bg-warning-text text-white'
                  : 'bg-white text-neutral-text hover:bg-neutral-panel disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
            >
              行単位削除
            </button>
            <button
              type="button"
              onClick={() => changeImportMode('delete')}
              disabled={!!preview || !canDelete}
              aria-pressed={importMode === 'delete'}
              className={`px-4 py-2 text-sm transition border-l border-neutral-line ${
                importMode === 'delete'
                  ? 'bg-warning-text text-white'
                  : 'bg-white text-neutral-text hover:bg-neutral-panel disabled:opacity-40 disabled:cursor-not-allowed'
              }`}
            >
              月単位削除
            </button>
          </div>
          {isDeleteRowsMode && (
            <div className="mt-2 p-3 rounded bg-warning-bg border border-warning-border text-xs text-warning-text flex gap-2">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                行単位削除は <strong>CSV の各行の識別キーで一致する既存行のみ削除</strong>します (数量・金額などの値列は無視)。
                手入力データや他 CSV で入ったデータは影響を受けません。
              </div>
            </div>
          )}
          {isDeleteMonthMode && (
            <div className="mt-2 p-3 rounded bg-warning-bg border border-warning-border text-xs text-warning-text flex gap-2">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                月単位削除は <strong>CSVに列挙された (店舗 × 月) の既存データを月ごと一括削除</strong>します。
                取り消せません。手入力データも削除対象になるため、行単位削除で目的を達成できる場合はそちらを推奨。
              </div>
            </div>
          )}
        </div>

        <div className="text-sm text-neutral-sub">{spec.summary}</div>

        {/* v0.2 コードキー化 (2026-07-31): CSV列名の破壊的変更を常時警告 (折り畳み仕様表示だけでは気づかれないため) */}
        {spec.formatChangeNotice && (
          <div className="p-3 rounded bg-warning-bg border border-warning-border text-xs text-warning-text flex gap-2">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <div>{spec.formatChangeNotice}</div>
          </div>
        )}

        {/* v0.1.3: rollback 非対応マスタの取込前警告 */}
        {spec.rollbackSupported === false && (
          <div className="p-3 rounded bg-warning-bg border border-warning-border text-xs text-warning-text flex gap-2">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              <strong>この種別は rollback 非対応</strong>です。取込後は自動巻き戻しができません。取込前に <strong>取込フォーマット仕様</strong> と CSV 内容を必ずご確認ください。
              {spec.category === 'master' && ' 誤取込に備えて事前に D1 バックアップを取ることも推奨します (docs/OPERATIONS.md)。'}
            </div>
          </div>
        )}

        {/* 仕様表示（折り畳み） */}
        <div>
          <button
            onClick={() => setShowSpec((v) => !v)}
            className="flex items-center gap-1 text-sm text-brand-red hover:underline"
          >
            <Info size={14} /> {showSpec ? '取込フォーマット仕様を隠す' : '取込フォーマット仕様を表示'}
          </button>

          {showSpec && (
            <div className="mt-3 space-y-3">
              <div className="border border-neutral-line rounded overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-panel text-neutral-sub">
                    <tr>
                      <th className="text-left px-2 py-2 whitespace-nowrap">列名</th>
                      <th className="text-center px-2 py-2 w-12">必須</th>
                      <th className="text-left px-2 py-2 w-20">型</th>
                      <th className="text-left px-2 py-2">説明</th>
                      <th className="text-left px-2 py-2">例</th>
                      <th className="text-left px-2 py-2">注意事項</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spec.columns.map((col) => (
                      <tr key={col.name} className="border-t border-neutral-line">
                        <td className="px-2 py-1.5 font-mono text-brand-red whitespace-nowrap">{col.name}</td>
                        <td className="px-2 py-1.5 text-center">
                          {col.required ? <span className="text-warning-text font-bold">●</span> : <span className="text-neutral-line">○</span>}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{col.type}</td>
                        <td className="px-2 py-1.5">{col.description}</td>
                        <td className="px-2 py-1.5 font-mono text-neutral-sub whitespace-nowrap">{col.example}</td>
                        <td className="px-2 py-1.5 text-neutral-sub">{col.notes ?? '−'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-neutral-panel rounded p-3 text-xs space-y-1">
                <div className="font-medium text-neutral-text">共通注意事項</div>
                <ul className="list-disc pl-5 text-neutral-sub space-y-0.5">
                  {spec.commonNotes.map((n) => <li key={n}>{n}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSVアップロード */}
      <div className="border border-neutral-line rounded-lg bg-white p-6 space-y-4">
        <div>
          <div className="border-2 border-dashed border-neutral-line rounded-lg p-8 text-center">
            <Upload size={32} className="mx-auto mb-2 text-neutral-sub" />
            <input type="file" accept=".csv,text/csv"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setPreview(null); setCommitResult(null); setPendingApproval(null); setMessage(null); }}
              disabled={!!preview}
              className="mx-auto" />
            {file && <div className="text-sm text-neutral-sub mt-2">選択中: {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
          </div>
        </div>

        {!preview && (
          <div className="flex justify-end">
            <button onClick={() => uploadMut.mutate()} disabled={!uploadPayload || uploadMut.isPending}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {uploadMut.isPending ? '検証中…' : 'アップロード＆プレビュー'}
            </button>
          </div>
        )}
      </div>

      {message && (
        // Design H-2 反映: 動的に更新される成功/警告メッセージを SR に通知
        <div
          role="status"
          aria-live="polite"
          className={`p-3 rounded border ${message.startsWith('✅')
            ? 'bg-green-50 text-green-800 border-green-200'
            : 'bg-warning-bg text-warning-text border-warning-border'}`}
        >
          {message}
        </div>
      )}

      {pendingApproval && (
        <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-1">
          <div className="flex items-center gap-2 text-neutral-text font-medium">
            <Info size={16} className="text-brand-orange flex-shrink-0" />
            承認待ちです
          </div>
          <div className="text-sm text-neutral-sub">
            この取込は承認ワークフローの対象です。承認者による承認後にコミットされます。
          </div>
          <div className="text-xs text-neutral-sub">
            申請ID: <span className="font-mono">{pendingApproval.requestId}</span>
          </div>
        </div>
      )}

      <ImportResultBoundary>
      {commitResult && commitResult.errors.length > 0 && (
        <div className="border border-neutral-line rounded-lg bg-white p-6 space-y-4">
          {/* batch_failure 検知時は原因説明バナーを表示 (Design H-1/H-2 対応)
              backend が code 別に集約済みのため、代表エントリを 1 件抽出して技術メッセージを日本語文脈に翻訳 */}
          {(() => {
            const bfEntries = commitResult.errors.filter((e) => e.code === 'batch_failure');
            if (bfEntries.length === 0) return null;
            const totalBf = bfEntries.reduce((s, e) => s + (e.aggregated_count ?? 1), 0);
            const sample = bfEntries[0]!;
            return (
              <div className="p-4 rounded border border-warning-border bg-warning-bg">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} className="text-warning-text flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="font-semibold text-warning-text">
                      取込を中止しました ({totalBf} 行が影響)
                    </div>
                    <div className="text-sm text-neutral-text">
                      データ整合性エラー (FK 違反等) を検出したため、安全のため取込対象行を全てロールバックしました。既存データは変更されていません。
                    </div>
                    <div className="text-xs text-neutral-sub font-mono">
                      詳細: {sample.message}
                    </div>
                    <div className="text-sm text-neutral-sub pt-1">
                      対策: 原因行を CSV から取り除くか、参照先マスタ (メニュー・食材・デリバリー業者等) を先に登録してから再取込してください。
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          <ImportErrorTable errors={commitResult.errors} totalErrorCount={commitResult.error_count} />
        </div>
      )}

      {/* 取込直後の rollback ボタン (upsert かつ inserted > 0 の場合のみ表示) */}
      {commitResult && commitResult.mode !== 'delete' && commitResult.mode !== 'delete_rows'
        && commitResult.inserted > 0 && (
        <div className="border border-neutral-line rounded-lg bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-neutral-sub">
            この取込を取り消したい場合は右のボタンで実行できます (取込先のデータのみ削除、上書き前のデータは復元されません)
          </div>
          <button
            type="button"
            onClick={() => setRollbackConfirmOpen(true)}
            disabled={rollbackMut.isPending}
            className="flex items-center gap-1 px-3 py-2 rounded border border-warning-text text-warning-text text-sm hover:bg-warning-bg disabled:opacity-50"
          >
            <Undo2 size={14} /> この取込を取消 (rollback)
          </button>
        </div>
      )}

      {/* v0.3 (S-8データ公開ゲート): 取込先の (店舗×月) を一括で入力完了にする。
          replacementsが無いケース (初回insertでreplace対象0件等) では店舗×月が特定できないため非表示 */}
      {commitResult && commitResult.mode !== 'delete' && commitResult.mode !== 'delete_rows'
        && commitResult.inserted > 0 && committedScope.length > 0
        && tenantFeatures?.publish_gate_enabled === true
        && hasPermissionForFeatureAction('import', 'manage') && (
        <div className="border border-neutral-line rounded-lg bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm text-neutral-sub">
            この取込分 ({committedScope.length}件の店舗×月) をまとめて入力完了にできます
          </div>
          <button
            type="button"
            disabled={markingReady || !allStores}
            onClick={async () => {
              setMarkingReady(true);
              const codeToId = new Map((allStores ?? []).map((s) => [s.store_code, s.store_id]));
              let ok = 0;
              // 中間REVIEW (S-12 Test/Design) HIGH対応: 個別失敗を握りつぶさず、失敗した店舗コードを
              // 収集してユーザーに明示する (成功件数のみのtoastでは「どれが失敗したか」が伝わらない)
              const failedCodes: string[] = [];
              for (const scope of committedScope) {
                const nodeId = codeToId.get(scope.store_code);
                if (!nodeId) { failedCodes.push(scope.store_code); continue; }
                // eslint-disable-next-line no-await-in-loop -- 通常数件〜数十件規模のため直列実行で許容
                try {
                  await postInputReady({ node_id: nodeId, target_month: scope.period.slice(0, 7), data_type: dataType, input_ready: true });
                  ok += 1;
                } catch {
                  failedCodes.push(scope.store_code);
                }
              }
              setMarkingReady(false);
              if (ok > 0) toast.success(`${ok}件を入力完了にしました`);
              if (failedCodes.length > 0) {
                toast.error(`${failedCodes.length}件が失敗しました (店舗コード: ${failedCodes.join('、')})`);
              }
            }}
            className="flex items-center gap-1 px-3 py-2 rounded bg-success-bg text-success-text border border-success-border text-sm hover:opacity-90 disabled:opacity-50"
          >
            <CheckCircle2 size={14} /> {markingReady ? '処理中…' : '入力完了にする'}
          </button>
        </div>
      )}
      </ImportResultBoundary>

      {preview && (
        <div className="border border-neutral-line rounded-lg bg-white p-6 space-y-4">
          <h2 className="text-h2">取込プレビュー</h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 rounded bg-neutral-panel">
              <div className="text-xs text-neutral-sub">全行数</div>
              <div className="text-h2 tabular-nums">{preview.total_rows}</div>
            </div>
            <div className="p-3 rounded bg-green-50">
              <div className="text-xs text-neutral-sub">有効</div>
              <div className="text-h2 tabular-nums text-green-800">{preview.valid_rows}</div>
            </div>
            <div className={`p-3 rounded ${preview.invalid_rows > 0 ? 'bg-warning-bg' : 'bg-neutral-panel'}`}>
              <div className="text-xs text-neutral-sub flex items-center justify-center gap-1">
                {preview.invalid_rows > 0 && <AlertTriangle size={12} className="text-warning-text" />}
                エラー
              </div>
              <div className={`text-h2 tabular-nums ${preview.invalid_rows > 0 ? 'text-warning-text' : ''}`}>{preview.invalid_rows}</div>
            </div>
          </div>

          {preview.mode === 'delete_rows' && (
            <div className="p-3 rounded bg-warning-bg border border-warning-text">
              <div className="font-medium text-warning-text mb-1 flex items-center gap-1.5">
                <AlertTriangle size={16} />
                行単位削除で <strong>{preview.row_delete_matches ?? 0} 件</strong> の DB 行が削除されます (CSV {preview.valid_rows} 行と一致)
              </div>
              <div className="text-xs text-warning-text mb-1">
                CSVの各行と識別キーが一致する既存行のみ削除します。取り消しできません。
                手入力データや他 CSV で入ったデータは影響を受けません。
              </div>
              {preview.row_delete_matches === 0 && (
                <div className="text-xs text-warning-text mt-1">
                  <strong>一致する既存行が 0 件です</strong> — CSV の識別キー (店舗コード / 日付 / 品目名など) が既存データと合致しているか確認してください。
                </div>
              )}
            </div>
          )}

          {preview.replacements && preview.replacements.length > 0 && preview.mode !== 'delete_rows' && (
            <div className={`p-3 rounded border ${
              preview.mode === 'delete'
                ? 'bg-warning-bg border-warning-text'
                : 'bg-warning-bg border-warning-border'
            }`}>
              <div className="font-medium text-warning-text mb-1 flex items-center gap-1.5">
                <AlertTriangle size={16} />
                {preview.mode === 'delete'
                  ? `以下 (店舗 × 月) を一括削除します (合計 ${preview.replacements.reduce((sum, r) => sum + r.existing_count, 0)} 件)`
                  : '既存データが置換されます'}
              </div>
              <div className="text-xs text-warning-text mb-2">
                {preview.mode === 'delete'
                  ? '取り消しできません。実行前に対象キーを再確認してください。手入力データも削除対象になります。'
                  : '以下の (店舗 × 月) の組み合わせで、既存データを削除してから新しいデータを取込みます。CSVに含まれない (店舗×月) の組み合わせは影響を受けません。'}
              </div>
              {preview.mode !== 'delete' && (dataType === 'delivery_details' || dataType === 'delivery_sales') && (
                <div className="text-xs text-warning-text mb-2 font-medium">
                  ⚠️ このCSVに一部の食材・業者コードしか含まれていない場合、その (店舗×月) の他の食材・業者の既存データも全て削除されます。
                  一部だけを修正したい場合は、対象月の完全なCSV (全食材・全業者分) を作り直してから取込んでください。
                </div>
              )}
              <div className="border border-warning-border rounded overflow-hidden max-h-40 overflow-y-auto">
                <table className="w-full text-xs bg-white">
                  <thead className="bg-warning-bg/50 text-warning-text">
                    <tr>
                      <th className="text-left px-3 py-1.5">店舗コード</th>
                      <th className="text-left px-3 py-1.5">対象月</th>
                      <th className="text-right px-3 py-1.5">
                        {preview.mode === 'delete' ? '削除対象件数' : '削除される件数'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.replacements.map((r, i) => (
                      <tr key={i} className="border-t border-warning-border/40">
                        <td className="px-3 py-1 tabular-nums">{r.store_code}</td>
                        <td className="px-3 py-1 tabular-nums">{r.period.slice(0, 7)}</td>
                        <td className="px-3 py-1 text-right tabular-nums">{r.existing_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {dataType === 'stores' && preview.store_operation_counts
            && (preview.store_operation_counts.renew + preview.store_operation_counts.close + preview.store_operation_counts.reopen) > 0 && (
            <div className="p-3 rounded border bg-warning-bg border-warning-text">
              <div className="font-medium text-warning-text mb-1 flex items-center gap-1.5">
                <AlertTriangle size={16} />
                renew/close/reopen 操作を含む CSV です
              </div>
              <div className="text-xs text-warning-text">
                {preview.store_operation_counts.renew > 0 && (
                  <div>renew (リニューアル・別店舗扱い): {preview.store_operation_counts.renew} 件 — 対象店舗は現在の店舗を閉店し、同じ店舗コードで新しい店舗IDとして登録し直されます</div>
                )}
                {preview.store_operation_counts.close > 0 && (
                  <div>close (閉店): {preview.store_operation_counts.close} 件 — 対象店舗は閉店扱いになり、ダッシュボード集計対象から外れます</div>
                )}
                {preview.store_operation_counts.reopen > 0 && (
                  <div>reopen (再開): {preview.store_operation_counts.reopen} 件 — 直近に閉店した店舗を再開します</div>
                )}
              </div>
            </div>
          )}

          {dataType === 'stores' && (preview.skipped_rows ?? 0) > 0 && (
            <div className="p-3 rounded bg-neutral-panel text-sm text-neutral-sub">
              閉店済みの店舗の行 {preview.skipped_rows} 件は、変更がないため取込みません
              (店舗マスタの CSV を出力して取込み直したときの閉店店舗の行です)。
              {preview.valid_rows === 0 && ' 取込む行はありません。'}
            </div>
          )}

          {preview.errors.length > 0 && (
            <ImportErrorTable errors={preview.errors} totalErrorCount={preview.invalid_rows} />
          )}

          {preview.preview.length > 0 && (
            <div>
              <h3 className="text-h3 mb-2">データプレビュー（先頭10行）</h3>
              <div className="border border-neutral-line rounded overflow-auto max-h-60">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-panel text-neutral-sub sticky top-0">
                    <tr>
                      {Object.keys(preview.preview[0] ?? {}).map((k) => (
                        <th key={k} className="text-left px-2 py-2">{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((r, i) => (
                      <tr key={i} className="border-t border-neutral-line">
                        {Object.values(r).map((v, j) => (
                          <td key={j} className="px-2 py-1">{String(v)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => { setPreview(null); setFile(null); }}
              className="flex items-center gap-1 px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">
              <X size={16} /> キャンセル
            </button>
            {preview.mode === 'delete_rows' ? (
              <button
                onClick={doCommit}
                disabled={commitMut.isPending || (preview.row_delete_matches ?? 0) === 0 || preview.invalid_rows > 0}
                className="flex items-center gap-1 px-4 py-2 rounded bg-warning-text text-white hover:opacity-90 disabled:opacity-50"
              >
                <Trash2 size={16} /> {commitMut.isPending ? '削除中…' : `行単位削除を実行 (${preview.row_delete_matches ?? 0} 件)`}
              </button>
            ) : preview.mode === 'delete' ? (
              <button
                onClick={doCommit}
                disabled={commitMut.isPending || (preview.replacements ?? []).length === 0 || preview.invalid_rows > 0}
                className="flex items-center gap-1 px-4 py-2 rounded bg-warning-text text-white hover:opacity-90 disabled:opacity-50"
              >
                <Trash2 size={16} /> {commitMut.isPending ? '削除中…' : '月単位削除を実行'}
              </button>
            ) : (
              <button
                onClick={doCommit}
                disabled={commitMut.isPending || preview.valid_rows === 0}
                className="flex items-center gap-1 px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50"
              >
                <Check size={16} /> {commitMut.isPending ? '取込中…' : '取込実行'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Rollback 確認モーダル */}
      {rollbackConfirmOpen && commitResult && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rollback-confirm-title"
        >
          <div className="bg-white rounded-lg max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-warning-text">
              <AlertTriangle size={24} />
              <h2 id="rollback-confirm-title" className="text-h2">取込の取消 (rollback)</h2>
            </div>
            <div className="text-sm space-y-2">
              <EnvironmentBadge variant="dialog" />
              <p>
                {/* Design H-1 反映: rollback は source_file_id 一致の INSERT 行のみ削除。inserted_rows があればそれを、無ければ後方互換で inserted を表示 */}
                この取込で追加された
                <strong className="text-warning-text"> {commitResult.inserted_rows ?? commitResult.inserted} 行</strong>
                を削除します。<strong>取り消しできません。</strong>
              </p>
              <div className="p-3 rounded bg-warning-bg border border-warning-border text-xs text-warning-text space-y-1">
                <div className="font-medium">重要: 上書き前データは復元されません</div>
                <div>
                  取込 (upsert) は既存の (店舗×月) データを削除してから新規挿入する仕様です。
                  rollback は<strong>新規挿入分のみ</strong>取り消します。取込前に存在していた元データは、この操作では戻せません。
                </div>
                {/* Design H-1 反映: UPDATE (旧版クローズ / マスタ更新) は rollback されない旨を明示 */}
                {(commitResult.updated_rows ?? 0) > 0 && (
                  <div>
                    ※ この取込で発生した<strong>更新 {commitResult.updated_rows} 件</strong> (メニュー旧版の有効期間クローズ or マスタ既存レコードの更新) も rollback では戻りません。
                  </div>
                )}
              </div>
              <div className="text-xs text-neutral-sub">
                ジョブID: <span className="font-mono">{commitResult.import_job_id}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setRollbackConfirmOpen(false)}
                className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel"
              >
                キャンセル
              </button>
              <button
                onClick={() => rollbackMut.mutate()}
                disabled={rollbackMut.isPending}
                className="flex items-center gap-1 px-4 py-2 rounded bg-warning-text text-white hover:opacity-90 disabled:opacity-50"
              >
                <Undo2 size={16} /> {rollbackMut.isPending ? '取消中…' : 'この取込を取消する'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 削除確認モーダル (2段構え) */}
      {deleteConfirmOpen && preview && (preview.mode === 'delete' || preview.mode === 'delete_rows') && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
        >
          <div className="bg-white rounded-lg max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center gap-2 text-warning-text">
              <AlertTriangle size={24} />
              <h2 id="delete-confirm-title" className="text-h2">
                {preview.mode === 'delete_rows' ? '行単位削除の確認' : '月単位一括削除の確認'}
              </h2>
            </div>
            <div className="text-sm space-y-2">
              <EnvironmentBadge variant="dialog" />
              {preview.mode === 'delete_rows' ? (
                <>
                  <p>
                    CSV の {preview.valid_rows} 行と一致する
                    <strong className="text-danger-text"> {preview.row_delete_matches ?? 0} 件</strong>
                    {' '}の既存 DB 行を削除します。<strong>取り消しできません。</strong>
                  </p>
                  <div className="text-xs text-neutral-sub">
                    識別キー (店舗コード / 日付 / 品目名など) で一致する行のみが対象です。手入力データや他 CSV で入ったデータは影響を受けません。
                  </div>
                </>
              ) : (
                <>
                  <p>
                    以下 <strong className="text-warning-text">{(preview.replacements ?? []).reduce((sum, r) => sum + r.existing_count, 0)} 件</strong>
                    {' '}({(preview.replacements ?? []).length} キー) を月ごと一括削除します。<strong>取り消しできません。</strong>
                    <span className="text-xs block mt-1 text-neutral-sub">手入力データも削除対象になります。</span>
                  </p>
                  <div className="max-h-40 overflow-y-auto border border-neutral-line rounded p-2 text-xs">
                    {(preview.replacements ?? []).map((r, i) => (
                      <div key={i} className="font-mono tabular-nums">
                        {r.store_code} / {r.period.slice(0, 7)} → {r.existing_count} 件
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel"
              >
                キャンセル
              </button>
              <button
                onClick={() => commitMut.mutate()}
                disabled={commitMut.isPending}
                className="flex items-center gap-1 px-4 py-2 rounded bg-warning-text text-white hover:opacity-90 disabled:opacity-50"
              >
                <Trash2 size={16} /> {commitMut.isPending ? '削除中…' : 'この内容で削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
